/**
 * Game card banner tags.
 *
 * A card carries a single `gameBanners` array (on IGameSettings) describing
 * which corner-banner signals apply to it — replacing the former separate
 * `topGame` / `newGame`  flags. The banner shown is the
 * highest-priority tag (see BANNER_PRIORITY), resolved at render time.
 *
 */

import { SECTION_NAMES } from '@repo/common';

// The banner tags a game card can carry.
export type BannerTag = 'hot' | 'cold' | 'top' | 'new';

// The hot/cold subset — a game is either hot or cold (used by the RTP banner
// and the Hot&Cold section builder).
export type HotColdBannerTag = 'hot' | 'cold';

export const BANNER_TAG = {
  HOT: 'hot',
  COLD: 'cold',
  TOP: 'top',
  NEW: 'new',
} as const;

// Which banner wins when a card has several tags — the first match is shown.
const BANNER_PRIORITY: BannerTag[] = [
  BANNER_TAG.HOT,
  BANNER_TAG.COLD,
  BANNER_TAG.TOP,
  BANNER_TAG.NEW,
];

// True when the tag is present on the card.
export const hasBannerTag = (banners: BannerTag[] | undefined, tag: BannerTag): boolean => {
  return (banners ?? []).includes(tag);
};

// Add or remove a tag from the card's banner array, returning the new array
export const setBannerTag = (
  banners: BannerTag[] | undefined,
  tag: BannerTag,
  shouldAdd: boolean,
): BannerTag[] => {
  const withoutTag = (banners ?? []).filter((t) => t !== tag);
  return shouldAdd ? [...withoutTag, tag] : withoutTag;
};

// Build the tag array from a list of candidates, keeping only the truthy ones.
export const buildGameBanners = (
  ...candidates: Array<BannerTag | false | null | undefined>
): BannerTag[] => {
  return candidates.filter((cand): cand is BannerTag => !!cand);
};

// The single banner to render for a card, or null if none applies.
export const resolveGameBanner = (
  banners: BannerTag[] | undefined,
  sectionName: string,
): BannerTag | null => {
  const tags = [...(banners ?? [])];

  // The New Releases section shows the NEW banner on every card in it.
  if (sectionName === SECTION_NAMES.NEW_REL && !tags.includes(BANNER_TAG.NEW)) {
    tags.push(BANNER_TAG.NEW);
  }

  // Return the highest-priority tag the card has.
  return BANNER_PRIORITY.find((tag) => tags.includes(tag)) ?? null;
};
